import React, { useState, useRef, useLayoutEffect } from 'react';
import {
	useFloating,
	useInteractions,
	useClick,
	useDismiss,
	useRole,
	autoUpdate,
	offset,
	flip,
	shift,
	FloatingFocusManager,
	FloatingPortal
} from '@floating-ui/react';

const Dropdown = ({
	value,
	placeholder,
	options,
	optionValue,
	onChange,
	menuClassName,
	disabled,
	displayValue,
	hideSelectedOption,
	buttonTextColor
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [menuWidth, setMenuWidth] = useState(undefined);
	const dropdownRef = useRef(null);

	const blurTrigger = () => {
		dropdownRef.current?.querySelector('.dropdown-ats-btn')?.blur();
	};

	const setOpen = open => {
		setIsOpen(open);
		if (!open) {
			// Clear mouse focus so the trigger doesn't stay in the hover look.
			blurTrigger();
		}
	};

	// Floating UI setup
	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setOpen,
		middleware: [offset(2), flip(), shift()],
		whileElementsMounted: autoUpdate,
		placement: 'bottom-start'
	});

	// Interaction hooks
	const click = useClick(context, {
		preventScroll: true
	});
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
		role
	]);

	const handleOptionClick = (option, event) => {
		event?.preventDefault?.();
		event?.stopPropagation?.();
		if (optionValue) {
			option = option[optionValue];
		}
		onChange(option);
		// Defer close so this click is fully consumed by the portal menu and
		// cannot fall through to a table row underneath when it unmounts.
		window.setTimeout(() => setOpen(false), 0);
	};

	const getValueText = () => {
		if (value !== undefined && optionValue) {
			const match = options.find(item => item[optionValue] === value);
			return match?.label || match?.text || placeholder;
		} else {
			return typeof value === 'string' && value !== '' ? value : placeholder;
		}
	};

	useLayoutEffect(() => {
		if (isOpen && dropdownRef.current) {
			setMenuWidth(dropdownRef.current.offsetWidth);
		}
	}, [isOpen]);

	const getOptionLabel = option => {
		if (option == null) {
			return '';
		}

		if (typeof option !== 'object') {
			return option;
		}

		return option.label ?? option.text ?? '';
	};

	const isOptionSelected = option => {
		if (option === undefined) {
			return false;
		}

		if (optionValue) {
			return option[optionValue] === value;
		}

		if (value === undefined || value === null) {
			return false;
		}

		if (
			typeof value === 'object' &&
			typeof option === 'object' &&
			value.value !== undefined &&
			option.value !== undefined
		) {
			return value.value === option.value;
		}

		return option === value;
	};

	const optionsList = hideSelectedOption
		? options.filter(option => {
				if (optionValue) {
					return option[optionValue] !== value;
				} else {
					if (
						typeof value === 'object' &&
						value !== null &&
						typeof option === 'object' &&
						option !== null
					) {
						if (value.value !== undefined && option.value !== undefined) {
							return option.value !== value.value;
						}
					}
					return option !== value;
				}
			})
		: options;

	return (
		<div className="dropdown-ats" ref={dropdownRef}>
			<button
				ref={refs.setReference}
				className={`dropdown-ats-btn ${value?.className || ''}`}
				style={{ color: buttonTextColor }}
				type="button"
				disabled={disabled}
				{...getReferenceProps({
					onClick: event => event.stopPropagation(),
					onMouseDown: event => event.stopPropagation()
				})}>
				<span className="dropdown-ats-btn-label">
					{displayValue
						? displayValue(getValueText())
						: value?.label || getValueText() || 'Select an option'}
				</span>
				{!disabled && (
					<svg className="chevron-down-icon" viewBox="0 0 22 22">
						<path
							d="M6 9l5 5 5-5"
							stroke="currentColor"
							strokeWidth="1"
							strokeLinecap="round"
							strokeLinejoin="round"
							fill="none"
						/>
					</svg>
				)}
			</button>
			<FloatingPortal>
				{isOpen && (
					<FloatingFocusManager
						context={context}
						modal={false}
						initialFocus={-1}
						returnFocus={false}>
						<ul
							ref={refs.setFloating}
							style={{
								...floatingStyles,
								minWidth: menuWidth
							}}
							{...getFloatingProps({
								onClick: event => event.stopPropagation(),
								onMouseDown: event => event.stopPropagation()
							})}
							className={`dropdown-ats-menu ${menuClassName || ''}`}>
							{optionsList?.map((option, index) => (
								<li
									key={index}
									className={`${option.className || ''} ${
										isOptionSelected(option) ? 'is-selected' : ''
									}`.trim()}
									onMouseDown={event => {
										// Prevent the click from falling through to a table
										// row underneath when the portal menu unmounts.
										event.preventDefault();
										event.stopPropagation();
									}}
									onClick={event => handleOptionClick(option, event)}>
									{getOptionLabel(option)}
								</li>
							))}
						</ul>
					</FloatingFocusManager>
				)}
			</FloatingPortal>
		</div>
	);
};

export default Dropdown;
